import { appendDebugLogLine } from './FileDebugLog';

export type DebugLogLevel = 'info' | 'warn' | 'error';

export type DebugLogEntry = {
  timestamp: string;
  level: DebugLogLevel;
  scope: string;
  message: string;
  data?: unknown;
};

const MAX_ENTRIES = 120;
const entries: DebugLogEntry[] = [];
const STORAGE_KEY = 'jrpg-launcher.debug-log.v1';

function persist() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {}
}

function restore() {
  if (typeof window === 'undefined') return;
  if (entries.length > 0) return;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as DebugLogEntry[];
    entries.push(...parsed.slice(-MAX_ENTRIES));
  } catch {}
}

function push(level: DebugLogLevel, scope: string, message: string, data?: unknown) {
  restore();
  const entry: DebugLogEntry = {
    timestamp: new Date().toISOString(),
    level,
    scope,
    message,
    data,
  };

  entries.push(entry);
  if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES);
  persist();

  const serializedData =
    data === undefined
      ? ''
      : ` ${typeof data === 'string' ? data : JSON.stringify(data)}`;
  appendDebugLogLine(`${entry.timestamp} [${level.toUpperCase()}] [${scope}] ${message}${serializedData}`);

  if (level === 'error') console.error(`[${scope}] ${message}`, data ?? '');
  else if (level === 'warn') console.warn(`[${scope}] ${message}`, data ?? '');
  else console.info(`[${scope}] ${message}`, data ?? '');

  if (typeof window !== 'undefined') {
    (window as Window & { __jrpgDebugLog?: { getEntries: () => DebugLogEntry[]; clear: () => void } }).__jrpgDebugLog = {
      getEntries: () => [...entries],
      clear: () => { entries.length = 0; },
    };
  }
}

export class DebugLog {
  static info(scope: string, message: string, data?: unknown) {
    push('info', scope, message, data);
  }

  static warn(scope: string, message: string, data?: unknown) {
    push('warn', scope, message, data);
  }

  static error(scope: string, message: string, data?: unknown) {
    push('error', scope, message, data);
  }

  static recent(limit = 8): DebugLogEntry[] {
    restore();
    return entries.slice(-limit);
  }

  static clear() {
    entries.length = 0;
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {}
    }
  }
}
